import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Plus, Search } from 'lucide-react'
import { getCustomers } from '../../api/master'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { downloadCsv } from '../accounting/reportsShared'
import { MasterTabs, customerName, placeLine } from './masterShared'

// Customers: the people and businesses you invoice and sell to on account.
export default function CustomerList() {
  const [search, setSearch] = useState('')
  const { data: rows = [], isLoading, error } = useQuery({ queryKey: ['customers'], queryFn: getCustomers })
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows
      .filter((c) => !term || [customerName(c), c.email, c.phone, c.city, c.state].join(' ').toLowerCase().includes(term))
      .sort((a, b) => customerName(a).localeCompare(customerName(b)))
  }, [rows, search])

  const exportCsv = () => downloadCsv('customers', [
    ['First name', 'Last name', 'Email', 'Phone', 'Address', 'City', 'State', 'Postal code', 'Country'],
    ...shown.map((c) => [c.first_name, c.last_name, c.email, c.phone, c.address, c.city, c.state, c.postal_code, c.country]),
  ])

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Customers</h1>
            <div className="ci-title-meta">The people and businesses you invoice.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          <Link className="ci-btn ci-btn-primary" to="/master/customers/new"><Plus size={15} />New customer</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search customers">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, phone or city…" />
        </label>
      </div>

      {error && <ErrorBanner message="Couldn’t load the customers." />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>Customer</th><th className="hide-sm">Email</th><th className="hide-sm">Phone</th><th className="hide-sm">Place</th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="4"><Spinner label="Loading customers…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="4"><div className="ci-empty">{rows.length ? 'No customers match.' : <>No customers yet. <Link to="/master/customers/new">Add your first customer</Link>.</>}</div></td></tr>
              ) : shown.map((c) => (
                <tr key={c.customer_id}>
                  <td>
                    <Link className="ci-strong mf-row-link" to={`/master/customers/${c.customer_id}`}>{customerName(c)}</Link>
                    <span className="cell-sub show-sm-block">{[c.email, c.phone].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td className="hide-sm">{c.email || <span className="ci-muted-cell">–</span>}</td>
                  <td className="hide-sm">{c.phone || <span className="ci-muted-cell">–</span>}</td>
                  <td className="hide-sm ci-muted-cell">{placeLine(c) || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination"><span className="ci-page-info">{shown.length} {shown.length === 1 ? 'customer' : 'customers'}</span></div>
      </div>
    </div>
  )
}
